import { Component, EventEmitter, Inject, Input, OnInit, Output } from '@angular/core';
import { FormArray, FormBuilder, FormGroup } from '@angular/forms';
import {
    AppNotificationService,
    AppNotificationType,
    ClientManagerInstanceToken,
    ClientManagerInterface,
    ConfigConstants,
    ConfigService,
    ENDPOINTS,
    isDefined,
    MaterialCheckModel,
    MaterialQuoteModel,
    MaterialUtilitiesService,
    ModalClass,
    ODataMaterialCheckModel,
    ODataMaterialPricingResponseModel,
    ODataMatLTextModel,
    OnlineOnlyService,
} from '@evorait/pace-core';
import { LogInstanceToken, LogInterface } from '@evorait/pace-logging';
import { TranslateService } from '@ngx-translate/core';
import { BsModalRef, BsModalService } from 'ngx-bootstrap/modal';
import { of } from 'rxjs';
import { catchError, map, take, tap } from 'rxjs/operators';
import { MaterialPricingModel } from '../material-pricing/material-pricing.model';
import { MaterialQuoteComponent } from '../material-quote/material-quote.component';
import { faEllipsisV } from '@fortawesome/free-solid-svg-icons';

@Component({
    selector: 'pace-material-order-pricing',
    templateUrl: './material-order-pricing.component.html',
    styleUrls: ['./material-order-pricing.component.scss'],
})
export class MaterialOrderPricingComponent implements OnInit {
    @Input()
    public model: MaterialPricingModel;

    @Input()
    public displayRepairPrice: boolean;

    @Input()
    public displayCustomerPrice: boolean;

    @Input()
    public displayMaterialQuote: boolean;

    @Output()
    public materialOrderPricingAdded = new EventEmitter<MaterialCheckModel[]>();

    private readonly globalStorageLocation: string[];

    public isLoading = false;
    public faEllipsisV = faEllipsisV;
    public strings: {
        header: string;
    };

    public readonly isNgmP58OrSA: boolean;
    public readonly isNgmAP0: boolean;
    public readonly displayQuantityInput: boolean;
    public readonly displayTs: boolean;
    public readonly displayNdf: boolean;

    public materialPricingForm: FormGroup;
    public materialPricingFormChildren: FormArray;

    public displaySparePartsLinkAsMainActionButton: boolean;
    public displayCheckAsMainActionButton: boolean;
    public displayRequestAsMainActionButton: boolean;
    public constructor(
        private readonly configService: ConfigService,
        private readonly translate: TranslateService,
        private readonly formBuilder: FormBuilder,
        public readonly modalInstance: BsModalRef,
        private readonly modalService: BsModalService,
        private readonly appNotificationService: AppNotificationService,
        private readonly materialUtilities: MaterialUtilitiesService,
        private readonly onlineOnlyService: OnlineOnlyService,
        @Inject(ClientManagerInstanceToken) private readonly clientManager: ClientManagerInterface,
        @Inject(LogInstanceToken) private readonly log: LogInterface,
    ) {
        this.isNgmP58OrSA = this.clientManager.isNgmP58OrSA();
        this.isNgmAP0 = this.clientManager.isNgmAP0();
        this.displayQuantityInput = this.isNgmP58OrSA;
        this.displayTs = this.isNgmP58OrSA;
        this.displayNdf = this.isNgmP58OrSA;
        this.globalStorageLocation = this.configService.getGlobalStorageLocationMatPricing();
    }

    public ngOnInit(): void {
        this.materialPricingFormChildren = this.formBuilder.array([]);
        this.materialPricingForm = this.formBuilder.group({
            formChildren: this.materialPricingFormChildren,
        });

        this.strings = {
            header: this.displayRepairPrice
                ? <string>this.translate.instant('MATERIAL_CUSTOMER_PRICING_TITLE')
                : <string>this.translate.instant('MATERIAL_AVAILABILITY_TITLE'),
        };

        // Determines which will be the main action button to be displayed
        this.evaluateMainActionButton();
    }

    /**
     * This function adds a material to the checklist
     */
    public addMaterial(): void {
        this.model.addMaterialToPartSet();
        this.evaluateMainActionButton();
    }

    /**
     * This function deletes a material from the checklist
     *
     * @param index index of the material to be deleted
     */
    public deleteMaterial(index: number): void {
        this.model.deleteMaterialFromPartSet(index);
        this.evaluateMainActionButton();
    }

    public check(): void {
        this.isLoading = true;
        let filteredPartSetItem: ODataMaterialCheckModel[];
        this.onlineOnlyService
            .post<ODataMaterialPricingResponseModel>(ENDPOINTS.MatPricing, this.model.getOutModel())
            .pipe(
                take(1),
                map((data: ODataMaterialPricingResponseModel) => {
                    if (
                        isDefined(data.PartSet) &&
                        typeof data.PartSet === 'object' &&
                        Array.isArray(data.PartSet.results)
                    ) {
                        this.model.partSet.value = [];
                        filteredPartSetItem = data.PartSet.results;
                        if (this.globalStorageLocation.length > 0) {
                            filteredPartSetItem = data.PartSet.results.filter((material) =>
                                this.globalStorageLocation.includes(material[ConfigConstants.materialFilterKey]),
                            );
                        }

                        filteredPartSetItem.forEach((result) => {
                            this.populatePartSet(result, data.LtextSet.results);
                        });
                    }
                    this.evaluateMainActionButton();
                }),
                catchError((error) => {
                    this.log.error('MaterialOrderPricingComponent: check: error', error);
                    this.appNotificationService.setAppNotification(
                        AppNotificationType.ERROR,
                        <string>this.translate.instant('CUSTOMER_PRICING_REQUEST_ERROR_TITLE'),
                        `${<string>this.translate.instant('MATERIAL_REQUEST_ERROR_BODY')} ${JSON.stringify(error)}`,
                    );

                    return of(undefined);
                }),
                tap(() => (this.isLoading = false)),
            )
            .subscribe();
    }

    /**
     * This function creates MaterialCheckModel object from material and adds to partSet model
     *
     * @param material the material to be added
     * @param longTextSet the long text set
     */
    private populatePartSet(material: ODataMaterialCheckModel, longTextSet: ODataMatLTextModel[]): void {
        const resultedMaterial = new MaterialCheckModel(
            material.Material,
            parseInt(material.Quantity, 10),
            material.StorageLoc,
        );

        resultedMaterial.updateFromResponse(material, longTextSet);
        this.model.partSet.addModel(resultedMaterial);
    }

    /**
     * Opens material order
     */
    public order(): void {
        this.materialOrderPricingAdded.next(
            this.materialUtilities.getMaterialsWithGlobalStorageLocation(this.model.partSet.value),
        );
        this.materialOrderPricingAdded.complete();
        this.modalInstance.hide();
    }

    /**
     * Opens material quote
     */
    public createQuote(): void {
        // Once the send order is successful the modal should not be displayed.
        this.modalInstance.hide();
        this.modalService.show(MaterialQuoteComponent, {
            backdrop: 'static',
            class: ModalClass.MODAL_LARGE,
            initialState: {
                model: new MaterialQuoteModel(
                    undefined,
                    this.model.notificationModel,
                    this.materialUtilities.getMaterialsWithGlobalStorageLocation(this.model.partSet.value),
                ),
            },
            ariaLabelledBy: 'material-quote-modal-title',
        });
    }

    /**
     *  Logic to handle which action will be displayed as the main
     *  action button for mobile view. Other actions will be displayed in the drop up menu
     */
    private evaluateMainActionButton(): void {
        this.displaySparePartsLinkAsMainActionButton = this.model.partSet.value.length === 0;
        this.displayCheckAsMainActionButton =
            this.model.partSet.value.length !== 0 && this.model.partSet.value.some((part) => !part.available);
        this.displayRequestAsMainActionButton =
            this.model.partSet.value.length !== 0 && this.model.partSet.value.every((part) => part.available);
    }
}
